import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class RedirectService {
  private readonly REDIRECT_URL_KEY = 'pay_kd_redirect_url';
  private readonly BLOCKED_QUERY_MARKERS = [
    'a=login',
    'a=register',
    'a=forgot-password',
    'a=reset-password',
  ];
  private readonly BLOCKED_PATH_PREFIXES = [
    '/auth',
    '/admin-auth',
    '/support-login',
    '/superadmin',
    '/governor',
    '/tsp',
    '/mdamanager',
    '/taxareaoffice-login',
  ];

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  private normalizeUrl(url: string | null | undefined): string {
    return (url || '').trim();
  }

  private shouldPersistRedirect(url: string): boolean {
    const normalizedUrl = this.normalizeUrl(url);
    if (
      !normalizedUrl ||
      normalizedUrl === '/' ||
      !normalizedUrl.startsWith('/')
    ) {
      return false;
    }

    const lowerUrl = normalizedUrl.toLowerCase();

    const hasBlockedQuery = this.BLOCKED_QUERY_MARKERS.some((marker) =>
      lowerUrl.includes(marker),
    );
    if (hasBlockedQuery) {
      return false;
    }

    return !this.BLOCKED_PATH_PREFIXES.some((prefix) =>
      lowerUrl.startsWith(prefix),
    );
  }

  /**
   * Saves the current URL to redirect to after login
   * @param url - The URL to save
   */
  saveRedirectUrl(url: string): void {
    const normalizedUrl = this.normalizeUrl(url);
    if (this.shouldPersistRedirect(normalizedUrl)) {
      console.log('Saving redirect URL:', normalizedUrl);
      if (isPlatformBrowser(this.platformId)) {
        sessionStorage.setItem(this.REDIRECT_URL_KEY, normalizedUrl);
        console.log(
          'Saved to sessionStorage:',
          sessionStorage.getItem(this.REDIRECT_URL_KEY),
        );
      }
    } else {
      console.log('Not saving redirect URL (filtered out):', normalizedUrl);
    }
  }

  /**
   * Gets the saved redirect URL
   * @returns The saved URL or null if not found
   */
  getRedirectUrl(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      const url = this.normalizeUrl(
        sessionStorage.getItem(this.REDIRECT_URL_KEY),
      );
      if (!this.shouldPersistRedirect(url)) {
        this.clearRedirectUrl();
        console.log('Stored redirect URL is invalid and was cleared:', url);
        return null;
      }

      console.log('Getting redirect URL from sessionStorage:', url);
      return url;
    }
    return null;
  }

  /**
   * Clears the saved redirect URL
   */
  clearRedirectUrl(): void {
    console.log('Clearing redirect URL');
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.removeItem(this.REDIRECT_URL_KEY);
    }
  }

  /**
   * Gets and clears the redirect URL in one operation
   * @returns The saved URL or null if not found
   */
  getAndClearRedirectUrl(): string | null {
    const url = this.getRedirectUrl();
    console.log('Get and clear redirect URL:', url);
    if (url) {
      this.clearRedirectUrl();
    }
    return url;
  }
}
